import React, { useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { getEvent, addEvent, updateEvent, clearCurrentEvent } from '../actions/eventActions';
import { getPrograms } from '../actions/programActions';
import EventForm from '../components/content-management/EventForm';

import EventFormSkeleton from '../components/content-management/views/FormView/EventFormSkeleton';
import AdminLayout from '../components/settings/AdminLayout';
import { X } from 'lucide-react';
import { isAdminRole } from '../utils/roleHelpers';

const EventEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { currentEvent, eventLoading } = useSelector(state => state.event);
  const { programs } = useSelector(state => state.program);
  const { user } = useSelector(state => state.auth);

  // Determine mode based on URL
  const isViewMode = location.pathname.includes('/view');
  const isEditMode = location.pathname.includes('/edit');
  const isNewMode = !id;

  const isAdmin = isAdminRole(user?.role);
  const isInstructor = user?.role === 'instructor';
  const isReadOnly = isInstructor || isViewMode;

  useEffect(() => {
    const loadData = async () => {
      try {
        // Always load programs for the form
        await dispatch(getPrograms());

        // If we have an ID, load the event
        if (id) {
          await dispatch(getEvent(id));
        }
      } catch (error) {
        console.error('Error loading event:', error);
      }
    };

    loadData();

    // Cleanup on unmount
    return () => {
      dispatch(clearCurrentEvent());
    };
  }, [dispatch, id]);

  const handleSubmit = async (eventData) => {
    try {
      const action = id ? updateEvent(id, eventData) : addEvent(eventData);
      await dispatch(action);
      // Redirect back to events list
      navigate('/settings', { state: { activeTab: 'Events & Info' } });
    } catch (error) {
      console.error('Error saving event:', error);
    }
  };

  const handleClose = () => {
    navigate('/settings', { state: { activeTab: 'Events & Info' } });
  };

  // Show loading state
  if (eventLoading && id) {
    return (
      <AdminLayout activeTab="Events & Info">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="h-8 w-48 bg-gray-200 rounded animate-shimmer"></div>
            <button
              type="button"
              onClick={() => navigate('/settings', { state: { activeTab: 'Events & Info' } })}
              className="text-gray-400 hover:text-[#1086a4] focus:outline-none transition-colors"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          {/* Form Skeleton */}
          <EventFormSkeleton />
        </div>
      </AdminLayout>
    );
  }

  // If trying to edit/view an event that doesn't exist
  if (id && !currentEvent && !eventLoading) {
    return (
      <AdminLayout activeTab="Events & Info">
        <div className="flex flex-col items-center justify-center py-12">
          <div className="text-red-500 text-xl mb-4">Event not found</div>
          <button
            onClick={() => navigate('/settings', { state: { activeTab: 'Events & Info' } })}
            className="px-4 py-2 bg-[#1086a4] text-white rounded-lg hover:bg-[#054581] transition-colors"
          >
            Return to Events
          </button>
        </div>
      </AdminLayout>
    );
  }

  const mode = isViewMode ? 'view' : isEditMode ? 'edit' : 'new';

  return (
    <AdminLayout activeTab="Events & Info">
      <EventForm
        mode={mode}
        event={currentEvent}
        programs={programs}
        userRole={user?.role}
        onSubmit={handleSubmit}
        onClose={handleClose}
      />
    </AdminLayout>
  );
};

export default EventEditor;
